// ═══════════════════════════════════════════════════════════════
// LmsLessonModal — création / édition d'une leçon (champs par type)
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { FileText, Video, File as FileIcon, MessageSquare, ClipboardCheck, Save, Eye, Server, Link2, Plus, Trash2, Clock, Bookmark } from 'lucide-react';
import { lmsApi } from '../../services/lmsApi';
import { toast } from '../ui/Toast';
import { Modal, Field, TextInput, TextArea, Select, s, Spinner } from './lmsUi';

const TYPES = [
    { v: 'text', l: 'Texte', icon: FileText, hint: 'Contenu rédigé en markdown.' },
    { v: 'video', l: 'Vidéo', icon: Video, hint: 'Une vidéo hébergée (YouTube, Vimeo, MP4…).' },
    { v: 'file', l: 'Fichier', icon: FileIcon, hint: 'Une ressource téléchargeable du projet.' },
    { v: 'chat_task', l: 'Tuteur IA', icon: MessageSquare, hint: 'Un échange guidé avec le tuteur IA nourri par les bases du cours.' },
    { v: 'assessment', l: 'Évaluation', icon: ClipboardCheck, hint: 'Un quiz ou une copie notée.' },
];

const SELFHOST_PREFIX = 'selfhost:';

// Conversions horodatage : secondes ↔ « m:ss » (édition des moments-clés & chapitres).
const newRowId = () => `k${Math.random().toString(36).slice(2, 9)}`;
const secToMmss = (s) => { const n = Math.max(0, Math.round(Number(s) || 0)); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; };
const mmssToSec = (str) => {
    const t = String(str == null ? '' : str).trim();
    if (/^\d+$/.test(t)) return parseInt(t, 10);
    const m = t.match(/^(\d+):([0-5]?\d)$/);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 0;
};
const initialVideo = lesson => {
    const v = lesson?.video_url || '';
    if (v.startsWith(SELFHOST_PREFIX)) return { source: 'selfhost', key: v.slice(SELFHOST_PREFIX.length), url: '' };
    if (v) return { source: 'external', key: '', url: v };
    return { source: 'selfhost', key: '', url: '' }; // défaut souverain : auto-hébergé H2
};

export default function LmsLessonModal({ moduleId, moduleTitle, lesson, assessments = [], projectId, onClose, onSaved }) {
    const editing = !!lesson;
    const iv = initialVideo(lesson);
    const [f, setF] = useState({
        title: lesson?.title || '',
        content_type: lesson?.content_type || 'text',
        body_markdown: lesson?.body_markdown || '',
        video_source: iv.source,   // 'selfhost' | 'external'
        video_key: iv.key,         // clé H2, ex. formation/ep1.mp4
        video_url: iv.url,         // URL externe (YouTube/Vimeo/MP4)
        file_id: lesson?.file_id != null ? String(lesson.file_id) : '',
        file_ref: lesson?.file_ref || '',
        chat_prompt: lesson?.chat_prompt || '',
        assessment_id: lesson?.assessment_id != null ? String(lesson.assessment_id) : '',
        duration_minutes: lesson?.duration_minutes != null ? String(lesson.duration_minutes) : '',
        is_preview: !!lesson?.is_preview,
        resources: Array.isArray(lesson?.resources) ? lesson.resources : [],
        // Moments-clés (calque soft) & chapitres : horodatage édité en « m:ss ».
        key_moments: (Array.isArray(lesson?.key_moments) ? lesson.key_moments : [])
            .map((m) => ({ id: m.id || newRowId(), start: secToMmss(m.start), end: secToMmss(m.end), text: m.text || '' })),
        chapters: (Array.isArray(lesson?.chapters) ? lesson.chapters : [])
            .map((c) => ({ id: newRowId(), start: secToMmss(c.start), title: c.title || '' })),
    });
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState(null); // null = pas encore chargé
    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
    const type = TYPES.find((t) => t.v === f.content_type);

    // Charge les fichiers du projet backing (picker fichier + ressources téléchargeables).
    useEffect(() => {
        if (files === null && projectId) {
            lmsApi.listProjectFiles(projectId).then(setFiles).catch(() => setFiles([]));
        }
    }, [files, projectId]);

    // ─── Ressources téléchargeables (transcript, slides, documents…) ───
    const RES_KINDS = [
        { v: 'transcript', l: 'Transcription' }, { v: 'slides', l: 'Slides' },
        { v: 'document', l: 'Document' }, { v: 'audio', l: 'Audio' }, { v: 'other', l: 'Autre' },
    ];
    const newResId = () => `r${Math.random().toString(36).slice(2, 9)}`;
    const addResource = () => set('resources', [...f.resources, { id: newResId(), label: '', kind: 'transcript', file_id: '', file_ref: '' }]);
    const updateResource = (id, k, v) => set('resources', f.resources.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
    const removeResource = (id) => set('resources', f.resources.filter((r) => r.id !== id));

    // ─── Moments-clés (calque soft synchronisé) & chapitres ───
    const addMoment = () => set('key_moments', [...f.key_moments, { id: newRowId(), start: '0:00', end: '0:00', text: '' }]);
    const updateMoment = (id, k, v) => set('key_moments', f.key_moments.map((m) => (m.id === id ? { ...m, [k]: v } : m)));
    const removeMoment = (id) => set('key_moments', f.key_moments.filter((m) => m.id !== id));
    const addChapter = () => set('chapters', [...f.chapters, { id: newRowId(), start: '0:00', title: '' }]);
    const updateChapter = (id, k, v) => set('chapters', f.chapters.map((c) => (c.id === id ? { ...c, [k]: v } : c)));
    const removeChapter = (id) => set('chapters', f.chapters.filter((c) => c.id !== id));

    const canSave = f.title.trim()
        && (f.content_type !== 'assessment' || f.assessment_id);

    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            let video_url = null;
            if (f.content_type === 'video') {
                if (f.video_source === 'selfhost') {
                    const key = f.video_key.trim().replace(/^selfhost:/, '').replace(/^\/+/, '');
                    video_url = key ? `${SELFHOST_PREFIX}${key}` : null;
                } else {
                    video_url = f.video_url.trim() || null;
                }
            }
            const payload = {
                title: f.title.trim(),
                content_type: f.content_type,
                body_markdown: f.content_type === 'text' ? (f.body_markdown || null) : null,
                video_url,
                file_id: f.content_type === 'file' && f.file_id ? parseInt(f.file_id, 10) : null,
                file_ref: f.content_type === 'file' ? (f.file_ref.trim() || null) : null,
                chat_prompt: f.content_type === 'chat_task' ? (f.chat_prompt || null) : null,
                assessment_id: f.content_type === 'assessment' && f.assessment_id ? parseInt(f.assessment_id, 10) : null,
                duration_minutes: f.duration_minutes === '' ? null : parseInt(f.duration_minutes, 10),
                is_preview: f.is_preview,
                resources: (f.resources || [])
                    .map((r) => ({
                        id: r.id, label: (r.label || '').trim(), kind: r.kind || 'other',
                        file_id: r.file_id ? parseInt(r.file_id, 10) : null,
                        file_ref: (r.file_ref || '').trim() || null,
                    }))
                    .filter((r) => r.file_id || r.file_ref),
                key_moments: f.content_type === 'video'
                    ? (f.key_moments || [])
                        .map((m) => ({ id: m.id, start: mmssToSec(m.start), end: mmssToSec(m.end), text: (m.text || '').trim() }))
                        .filter((m) => m.text)
                        .sort((a, b) => a.start - b.start)
                    : [],
                chapters: f.content_type === 'video'
                    ? (f.chapters || [])
                        .map((c) => ({ start: mmssToSec(c.start), title: (c.title || '').trim() }))
                        .filter((c) => c.title)
                        .sort((a, b) => a.start - b.start)
                    : [],
            };
            if (editing) {
                await lmsApi.updateLesson(lesson.id, payload);
                toast.success('Leçon mise à jour');
            } else {
                await lmsApi.addLesson(moduleId, payload);
                toast.success('Leçon ajoutée');
            }
            onSaved?.();
        } catch (e) {
            toast.error(e.message || 'Échec de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={editing ? 'Modifier la leçon' : 'Nouvelle leçon'}
            subtitle={moduleTitle ? `Module · ${moduleTitle}` : undefined}
            icon={type?.icon || FileText}
            onClose={onClose}
            busy={saving}
            width={600}
            footer={
                <>
                    <button style={s.ghostBtn} onClick={onClose} disabled={saving}>Annuler</button>
                    <button style={{ ...s.primaryBtn, opacity: canSave && !saving ? 1 : 0.5 }} onClick={save} disabled={!canSave || saving}>
                        {saving ? <Spinner /> : <Save size={14} />} {editing ? 'Enregistrer' : 'Ajouter'}
                    </button>
                </>
            }
        >
            <Field label="Titre de la leçon">
                <TextInput value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="Ex. Qu'est-ce que la monnaie ?" autoFocus />
            </Field>

            <Field label="Type de contenu" hint={type?.hint}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {TYPES.map((t) => (
                        <button key={t.v} type="button" onClick={() => set('content_type', t.v)} style={{ ...s.chip(f.content_type === t.v), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <t.icon size={13} /> {t.l}
                        </button>
                    ))}
                </div>
            </Field>

            {f.content_type === 'text' && (
                <Field label="Contenu (markdown)">
                    <TextArea value={f.body_markdown} onChange={(e) => set('body_markdown', e.target.value)} placeholder="# Titre&#10;&#10;Le corps de la leçon en markdown…" style={{ minHeight: 160 }} />
                </Field>
            )}
            {f.content_type === 'video' && (
                <>
                    <Field label="Source de la vidéo" hint={f.video_source === 'selfhost'
                        ? 'Auto-hébergée sur le nœud souverain ITRIIA (Hetzner #2) — accès réservé aux inscrits.'
                        : 'Vidéo externe (YouTube, Vimeo, ou MP4 direct).'}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" onClick={() => set('video_source', 'selfhost')}
                                style={{ ...s.chip(f.video_source === 'selfhost'), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Server size={13} /> Auto-hébergé (H2)
                            </button>
                            <button type="button" onClick={() => set('video_source', 'external')}
                                style={{ ...s.chip(f.video_source === 'external'), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Link2 size={13} /> Lien externe
                            </button>
                        </div>
                    </Field>
                    {f.video_source === 'selfhost' ? (
                        <Field label="Clé du fichier sur le nœud média" hint="Chemin relatif sous /opt/itriia-video/ — ex. formation/ep1.mp4 (déposé via scp/rsync).">
                            <TextInput value={f.video_key} onChange={(e) => set('video_key', e.target.value)} placeholder="formation/ep1.mp4" />
                        </Field>
                    ) : (
                        <Field label="URL de la vidéo">
                            <TextInput value={f.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="https://www.youtube.com/watch?v=…" />
                        </Field>
                    )}
                </>
            )}
            {f.content_type === 'file' && (
                <>
                    <Field label="Fichier du projet" hint={projectId
                        ? 'Choisis une ressource déjà déposée dans le projet du cours.'
                        : 'Projet backing inconnu — utilise le champ lien ci-dessous.'}>
                        {files === null && projectId ? (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '6px 0' }}><Spinner /> Chargement des fichiers…</div>
                        ) : (
                            <Select value={f.file_id} onChange={(e) => set('file_id', e.target.value)} disabled={!projectId || !(files && files.length)}>
                                <option value="">{files && files.length ? '— Sélectionner un fichier —' : 'Aucun fichier dans le projet'}</option>
                                {(files || []).map((file) => (
                                    <option key={file.id} value={file.id}>{file.original_name || file.filename || `Fichier #${file.id}`}</option>
                                ))}
                            </Select>
                        )}
                    </Field>
                    <Field label="…ou lien / chemin direct" hint="Repli si le support n'est pas dans le projet (URL http, NextCloud…).">
                        <TextInput value={f.file_ref} onChange={(e) => set('file_ref', e.target.value)} placeholder="https://… ou support-cours.pdf" />
                    </Field>
                </>
            )}
            {f.content_type === 'chat_task' && (
                <Field label="Consigne au tuteur IA" hint="Le tuteur démarre l'échange avec cette consigne, enrichi par les bases d'expertise du cours.">
                    <TextArea value={f.chat_prompt} onChange={(e) => set('chat_prompt', e.target.value)} placeholder="Ex. Aide l'apprenant à reformuler la différence entre monnaie-dette et monnaie pleine, en partant de ses mots." style={{ minHeight: 120 }} />
                </Field>
            )}
            {f.content_type === 'assessment' && (
                <Field label="Évaluation rattachée" hint={assessments.length ? 'Choisis une évaluation déjà créée dans l\'onglet Évaluations.' : 'Aucune évaluation pour ce cours — crée-en une d\'abord.'}>
                    <Select value={f.assessment_id} onChange={(e) => set('assessment_id', e.target.value)}>
                        <option value="">— Sélectionner —</option>
                        {assessments.map((a) => <option key={a.id} value={a.id}>{a.title} {a.use_spectral ? '(spectral)' : '(QCM)'}</option>)}
                    </Select>
                </Field>
            )}

            <Field label="Ressources à télécharger" hint="Transcription, slides, documents… affichés sous le contenu de la leçon.">
                {f.resources.length === 0 && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>Aucune ressource pour l'instant.</div>
                )}
                {f.resources.map((r) => (
                    <div key={r.id} style={{ border: '1px solid var(--card-border)', borderRadius: 10, padding: 10, marginBottom: 8, display: 'grid', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Select value={r.kind} onChange={(e) => updateResource(r.id, 'kind', e.target.value)} style={{ maxWidth: 150 }}>
                                {RES_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                            </Select>
                            <TextInput value={r.label} onChange={(e) => updateResource(r.id, 'label', e.target.value)} placeholder="Intitulé (ex. Transcription)" />
                            <button type="button" onClick={() => removeResource(r.id)} style={{ ...s.ghostBtn, padding: '0 10px' }} title="Retirer la ressource"><Trash2 size={14} /></button>
                        </div>
                        <Select value={r.file_id} onChange={(e) => updateResource(r.id, 'file_id', e.target.value)} disabled={!(files && files.length)}>
                            <option value="">{files && files.length ? '— Fichier du projet —' : 'Aucun fichier dans le projet'}</option>
                            {(files || []).map((file) => (
                                <option key={file.id} value={file.id}>{file.original_name || file.filename || `Fichier #${file.id}`}</option>
                            ))}
                        </Select>
                        <TextInput value={r.file_ref} onChange={(e) => updateResource(r.id, 'file_ref', e.target.value)} placeholder="…ou lien direct (https://…)" />
                    </div>
                ))}
                <button type="button" onClick={addResource} style={{ ...s.ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Plus size={14} /> Ajouter une ressource
                </button>
            </Field>

            {f.content_type === 'video' && (
                <>
                    <Field label="Moments-clés (calque vidéo)" hint="Une accroche courte s'affiche dans la vidéo entre les horodatages (m:ss). Proposés par l'IA au dépôt du rush, corrigeables ici.">
                        {f.key_moments.length === 0 && (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>Aucun moment-clé.</div>
                        )}
                        {f.key_moments.map((m) => (
                            <div key={m.id} style={{ border: '1px solid var(--card-border)', borderRadius: 10, padding: 10, marginBottom: 8, display: 'grid', gap: 8 }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <Clock size={14} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                                    <TextInput value={m.start} onChange={(e) => updateMoment(m.id, 'start', e.target.value)} placeholder="0:12" style={{ maxWidth: 80 }} />
                                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>→</span>
                                    <TextInput value={m.end} onChange={(e) => updateMoment(m.id, 'end', e.target.value)} placeholder="0:18" style={{ maxWidth: 80 }} />
                                    <button type="button" onClick={() => removeMoment(m.id)} style={{ ...s.ghostBtn, padding: '0 10px', marginLeft: 'auto' }} title="Retirer le moment"><Trash2 size={14} /></button>
                                </div>
                                <TextInput value={m.text} onChange={(e) => updateMoment(m.id, 'text', e.target.value)} placeholder="Accroche affichée (ex. « L'IA au service du représentant »)" />
                            </div>
                        ))}
                        <button type="button" onClick={addMoment} style={{ ...s.ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Plus size={14} /> Ajouter un moment-clé
                        </button>
                    </Field>

                    <Field label="Chapitres" hint="Barre de navigation cliquable sous la vidéo (horodatage m:ss + titre).">
                        {f.chapters.length === 0 && (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>Aucun chapitre.</div>
                        )}
                        {f.chapters.map((c) => (
                            <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                <Bookmark size={14} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                                <TextInput value={c.start} onChange={(e) => updateChapter(c.id, 'start', e.target.value)} placeholder="2:30" style={{ maxWidth: 80 }} />
                                <TextInput value={c.title} onChange={(e) => updateChapter(c.id, 'title', e.target.value)} placeholder="Titre du chapitre" />
                                <button type="button" onClick={() => removeChapter(c.id)} style={{ ...s.ghostBtn, padding: '0 10px' }} title="Retirer le chapitre"><Trash2 size={14} /></button>
                            </div>
                        ))}
                        <button type="button" onClick={addChapter} style={{ ...s.ghostBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Plus size={14} /> Ajouter un chapitre
                        </button>
                    </Field>
                </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'start' }}>
                <Field label="Durée (min)">
                    <TextInput type="number" min="0" step="1" value={f.duration_minutes} onChange={(e) => set('duration_minutes', e.target.value)} placeholder="ex. 12" />
                </Field>
                <Field label="Accès">
                    <button type="button" onClick={() => set('is_preview', !f.is_preview)} style={{ ...s.ghostBtn, width: '100%', justifyContent: 'flex-start', gap: 8, borderColor: f.is_preview ? '#8b5cf6' : 'var(--card-border)', color: f.is_preview ? '#8b5cf6' : 'var(--text)' }}>
                        <Eye size={14} /> {f.is_preview ? 'Aperçu gratuit (visible sans inscription)' : 'Réservé aux inscrits'}
                    </button>
                </Field>
            </div>
        </Modal>
    );
}
